import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import CsLineIcons from "../../cs-line-icons";

export function PageHeader({ eyebrow = "FeraSurvey", title, description, action = null }) {
  return (
    <header className="fera-page-header">
      <div>
        <p className="fera-eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {description ? <p className="fera-page-description">{description}</p> : null}
      </div>
      {action ? <div className="fera-page-action">{action}</div> : null}
    </header>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="fera-loading" role="status" aria-live="polite">
      <Spinner animation="border" size="sm" />
      <span>{label}&hellip;</span>
    </div>
  );
}

export function EmptyState({ title, detail, action = null, icon = "clipboard" }) {
  return (
    <div className="fera-empty-panel">
      <div className="fera-empty-icon" aria-hidden="true"><CsLineIcons icon={icon} size={38} /></div>
      <h2>{title}</h2>
      <p>{detail}</p>
      {action}
    </div>
  );
}

export function ErrorAlert({ error, onRetry = null }) {
  if (!error) return null;
  return (
    <Alert variant="danger" className="d-flex align-items-center justify-content-between gap-3" role="alert">
      <span>{error}</span>
      {onRetry ? <Button size="sm" variant="outline-danger" onClick={onRetry}>Try again</Button> : null}
    </Alert>
  );
}

export function StatusBadge({ status }) {
  const key = String(status || "draft").toLowerCase();
  const variants = {
    published: "success",
    closed: "secondary",
    archived: "dark",
    deleted: "danger",
    draft: "light",
    submitted: "success",
    saved: "info",
    active: "success",
    disabled: "secondary",
    queued: "info",
    processing: "info",
    completed: "success",
    failed: "danger",
    warning: "warning",
    expired: "secondary",
    cancelled: "secondary",
    sent: "success",
    opened: "info",
    started: "info",
    "dead-lettered": "danger",
  };
  const variant = variants[key] || "light";
  return (
    <Badge bg={variant} text={["light", "warning", "info"].includes(variant) ? "dark" : undefined}>
      {labelize(key)}
    </Badge>
  );
}

export function PlanLock({ children = "Paid", title = "Available on paid FeraSurvey plans" }) {
  return (
    <span className="fera-plan-lock" title={title}>
      <CsLineIcons icon="lock-off" size={14} />
      <span>{children}</span>
    </span>
  );
}

export function labelize(value) {
  return String(value || "").replace(/_/g, " ").replace(/\b\w/g, letter => letter.toUpperCase());
}

export function formatDate(value, fallback = "\u2014") {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? fallback
    : date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function Meter({ value = 0, maximum = 0, label }) {
  const unlimited = maximum === null || Number(maximum) < 0;
  const percentage = unlimited || !maximum ? 0 : Math.min((Number(value) / Number(maximum)) * 100, 100);
  const warning = !unlimited && maximum > 0 && percentage >= 80;
  const danger = !unlimited && maximum > 0 && percentage >= 100;
  return (
    <div className={`fera-meter${warning ? " is-warning" : ""}${danger ? " is-danger" : ""}`}>
      <div className="fera-meter-label">
        <span>{label}</span>
        <strong>{value} / {unlimited ? "Unlimited" : maximum}</strong>
      </div>
      <div
        className="fera-meter-track"
        role="progressbar"
        aria-label={label}
        aria-valuemin="0"
        aria-valuemax={unlimited ? undefined : maximum}
        aria-valuenow={value}
      >
        <span style={{ width: `${percentage}%` }} />
      </div>
    </div>
  );
}

export function UpgradeGrid({ access, title = "Choose more FeraSurvey capacity" }) {
  const options = access?.upgradeOptions || [];
  if (!options.length && !access?.upgradeUrl) return null;
  return (
    <section className="fera-panel mt-4" aria-labelledby="upgrade-heading">
      <div className="fera-panel-heading">
        <div>
          <p className="fera-eyebrow">Upgrade</p>
          <h2 id="upgrade-heading">{title}</h2>
        </div>
      </div>
      {options.length ? (
        <div className="fera-upgrade-grid">
          {options.map(option => (
            <article key={option.code || option.planCode}>
              <div>
                <strong>{option.name}</strong>
                <span>{option.priceLabel}</span>
              </div>
              <p>{option.allowance}</p>
              <a className="btn btn-outline-primary" href={option.url || access.upgradeUrl}>
                Review {option.name}
              </a>
            </article>
          ))}
        </div>
      ) : (
        <a className="btn btn-primary" href={access.upgradeUrl}>Review upgrade options</a>
      )}
    </section>
  );
}
